/**
 * 列表键盘导航公共 Hook
 * 统一处理 ArrowUp / ArrowDown / Space，支持吸顶偏移、底部加载更多
 */
import React, { useState, useEffect, useCallback } from "react";
import { isElementVisible } from "../utils";
import { useLatest } from "./useLatest";

export interface UseListKeyboardNavOptions {
    /** 列表容器 ref */
    containerRef: React.RefObject<HTMLElement | null>;
    /** 当前导航列表的 ref（如 listRef / flatListRef / navigationListRef），调用方需在列表数据变化时同步更新此 ref */
    listRef: React.RefObject<any[]>;
    /** 用于滚动定位的 data 属性名，如 'data-index' | 'data-contact-index' | 'data-org-index' */
    dataAttribute: string;
    /** 吸顶区域高度（如 sticky header），不传则使用 scrollIntoView */
    stickyHeaderOffset?: number;
    /** 是否有更多数据（底部加载更多场景），用 ref 避免闭包旧值 */
    hasMoreDataRef?: React.RefObject<boolean>;
    /** 到达底部时加载更多（传入目标 index）；调用方在数据加载完成后应调用 setHighlightedIndex(targetIndex) 以保持高亮一致 */
    onLoadMore?: (targetIndex: number) => void;
    /** 空格键选中/取消时回调 */
    onSelectItem: (index: number, item: any) => void;
    /** 判断某项是否禁用，禁用项不响应空格 */
    isItemDisabled?: (item: any) => boolean;
}

export function useListKeyboardNavigation(options: UseListKeyboardNavOptions) {
    const {
        containerRef,
        listRef,
        dataAttribute,
        stickyHeaderOffset,
        hasMoreDataRef,
        onLoadMore,
        onSelectItem,
        isItemDisabled,
    } = options;

    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    // 使用 useLatest 替代多个 useEffect 同步 ref 的反模式
    const highlightedIndexRef = useLatest(highlightedIndex);
    const onSelectItemRef = useLatest(onSelectItem);
    const onLoadMoreRef = useLatest(onLoadMore);
    const isItemDisabledRef = useLatest(isItemDisabled);

    const scrollToItem = useCallback((index: number) => {
        const container = containerRef.current;
        const selector = `[${dataAttribute}="${index}"]`;
        const element = container?.querySelector(selector) as HTMLElement;
        if (!container || !element) return;

        if (stickyHeaderOffset != null && stickyHeaderOffset > 0) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const elementTop = elementRect.top - containerRect.top;
            const elementBottom = elementRect.bottom - containerRect.top;
            if (elementTop < stickyHeaderOffset) {
                container.scrollTop += elementTop - stickyHeaderOffset;
            } else if (elementBottom > containerRect.height) {
                container.scrollTop += elementBottom - containerRect.height;
            }
        } else {
            element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [containerRef, dataAttribute, stickyHeaderOffset]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // 仅排除文本类输入（搜索框等），避免输入空格时误选中列表项
            const target = event.target as HTMLInputElement & HTMLElement;
            const tagName = target?.tagName?.toLowerCase();
            if (tagName === "textarea" || !!target?.isContentEditable) return;
            if (tagName === "input") {
                const type = (target as HTMLInputElement).type?.toLowerCase?.() || "text";
                const isTextLikeInput = [
                    "text",
                    "search",
                    "email",
                    "password",
                    "number",
                    "tel",
                    "url",
                ].includes(type);
                if (isTextLikeInput) return;
            }

            const element = containerRef.current;
            if (!element || !isElementVisible(element)) return;

            const list = listRef.current;
            if (!list || list.length === 0) return;

            const idx = highlightedIndexRef.current;
            const hasMore = hasMoreDataRef?.current ?? false;
            const effectiveIdx = idx < 0 ? (event.key === "ArrowUp" ? 0 : -1) : idx;

            switch (event.key) {
                case "ArrowUp": {
                    event.preventDefault();
                    const current = idx < 0 ? 0 : idx;
                    const atTop = current <= 0;
                    const next = atTop
                        ? (hasMore ? 0 : list.length - 1)
                        : current - 1;
                    setHighlightedIndex(next);
                    scrollToItem(next);
                    break;
                }
                case "ArrowDown": {
                    event.preventDefault();
                    const atEnd = effectiveIdx >= list.length - 1;
                    if (atEnd && hasMore && onLoadMoreRef.current) {
                        const targetIndex = effectiveIdx + 1;
                        onLoadMoreRef.current(targetIndex);
                    } else {
                        const current = idx < 0 ? -1 : idx;
                        const next = current >= list.length - 1 ? 0 : current + 1;
                        setHighlightedIndex(next);
                        scrollToItem(next);
                    }
                    break;
                }
                case " ":
                case "Spacebar":
                    event.preventDefault();
                    if (idx >= 0 && idx < list.length) {
                        const item = list[idx];
                        const disabled = isItemDisabledRef.current?.(item);
                        if (!disabled) {
                            onSelectItemRef.current(idx, item);
                        }
                    }
                    break;
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [containerRef, listRef, hasMoreDataRef, scrollToItem]);

    const handlePointerMove = useCallback((index: number) => {
        setHighlightedIndex(index);
    }, []);

    return { highlightedIndex, setHighlightedIndex, handlePointerMove, scrollToItem };
}
